import {
  AfterViewInit,
  Component,
  OnInit,
  Output,
  EventEmitter,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DeviceDetectorService } from 'ngx-device-detector';

import { IFilterNumber } from '@shared/models/graphql';
import { IPropertiesFilters } from '@shared/models/filters';
import { PROPERTIES_PER_PAGE } from '@shared/models/paginator';

import { Store } from '@ngrx/store';
import { AppState } from '@state/app.state';
import { fetchPropertiesAction } from '@state/properties/properties.actions';
import {
  resetFiltersAction,
  resetUnsavedFiltersAction,
  setFiltersAction,
} from '@state/filters/filters.actions';
import { selectFiltersCurrent } from '@state/filters/filters.selectors';
import { selectAuth } from '@state/auth/auth.selectors';

@UntilDestroy()
@Component({
  selector: 'app-shared-filters-v2',
  templateUrl: './filters-v2.component.html',
  styleUrls: ['./filters-v2.component.scss'],
})
export class FiltersV2Component implements OnInit, AfterViewInit {
  @Output() scrollToTop = new EventEmitter<void>();

  protected firstChange = true;
  protected filtersCount: string[] = [];
  protected filters?: IPropertiesFilters;

  public isMobile = false;
  protected openModal = false;
  protected isLoggedIn?: boolean = undefined;
  protected isConfirmed?: boolean = undefined;

  constructor(
    private store: Store<AppState>,
    private deviceService: DeviceDetectorService
  ) {}

  ngAfterViewInit(): void {
    this.applyFilters();
  }

  ngOnInit(): void {
    this.isMobile = this.deviceService.isMobile();

    if (!this.firstChange) {
      this.applyFilters();
    }

    this.store
      .select(selectFiltersCurrent)
      .pipe(untilDestroyed(this))
      .subscribe((filters) => {
        this.filters = filters;
      });

    // if user logs in or out, we need to reload the property
    // first time we don't need to reload it
    // if users confirm his account, we need to reload the properties
    this.store
      .select(selectAuth)
      .pipe(untilDestroyed(this))
      .subscribe((authData) => {
        const { token, user } = authData;
        if (
          (this.isLoggedIn !== undefined && this.isLoggedIn !== !!token) ||
          (this.isConfirmed !== undefined &&
            this.isConfirmed !== !!user?.emailConfirmedAt &&
            !!user?.phoneNumberConfirmedAt)
        ) {
          this.applyFilters();
        }
        this.isLoggedIn = !!token;
        this.isConfirmed =
          !!user?.emailConfirmedAt && !!user?.phoneNumberConfirmedAt;
      });
  }

  clearFilters(): void {
    this.filtersCount = [];
    this.store.dispatch(resetFiltersAction());
    this.applyFilters(false);
    this.openModal = false;
  }

  applyFilters(closeModal?: boolean): void {
    if (!this.firstChange) this.scrollToTop.emit();
    if (this.firstChange) this.store.dispatch(resetUnsavedFiltersAction());
    this.store.dispatch(setFiltersAction());
    this.handleFetchProperties(closeModal);
  }

  convertFilter(name: string, data: IFilterNumber) {
    const filter = [];
    if (data.eq !== undefined) {
      filter.push({
        and: {
          [name]: {
            eq: data.eq,
          },
        },
      });
    } else if (data.greaterThanOrEqual && data.lessThanOrEqual) {
      filter.push({
        and: [
          {
            [name]: {
              greaterThanOrEqual: data.greaterThanOrEqual,
            },
          },
          {
            [name]: {
              lessThanOrEqual: data.lessThanOrEqual,
            },
          },
        ],
      });
    } else if (data.greaterThanOrEqual) {
      filter.push({
        or: [
          { [name]: { isNil: true } },
          {
            [name]: {
              greaterThanOrEqual: data.greaterThanOrEqual,
            },
          },
        ],
      });
    } else if (data.lessThanOrEqual) {
      filter.push({
        or: [
          { [name]: { isNil: true } },
          {
            [name]: {
              lessThanOrEqual: data.lessThanOrEqual,
            },
          },
        ],
      });
    }
    return filter;
  }

  handleFetchProperties(closeModal?: boolean): void {
    const element = document.getElementById('home-list-view-aside');
    if (element) element.scroll({ top: 0, left: 0 });
    try {
      const filters: any = { and: [] };
      // STATUS
      filters.and.push({
        and: [
          {
            status: this.filters?.status || { in: ['OPEN', 'PENDING', 'SOLD'] },
          },
        ],
      });
      // IS VERIFIED
      if (this.filters?.isVerified && !!this.filters.isVerified.eq) {
        filters.and.push({
          and: {
            isVerified: this.filters.isVerified,
          },
        });
        // If isVerified is false and there are no statuses selected,
        // send isVerified false to show all 3rd party listings
      } else if ((this.filters?.status?.in?.length || 0) === 0) {
        filters.and.push({
          and: {
            isVerified: { eq: false },
          },
        });
      }

      // PRICE
      if (this.filters?.price) {
        filters.and.push(...this.convertFilter('price', this.filters.price));
      }
      // PRO FORMA
      if (this.filters?.proForma) {
        const keys = Object.keys(this.filters.proForma);
        if (keys.length > 0) {
          filters.and.push({
            and: [
              {
                proForma: this.filters.proForma,
              },
            ],
          });
        }
      }
      // BEDROOMS
      if (this.filters?.bedrooms) {
        filters.and.push(
          ...this.convertFilter('bedrooms', this.filters.bedrooms)
        );
      }
      // BATHROOMS
      if (this.filters?.bathrooms) {
        filters.and.push(
          ...this.convertFilter('bathrooms', this.filters.bathrooms)
        );
      }
      // SUBTYPE
      if (
        this.filters?.subType &&
        this.filters.subType.in &&
        this.filters.subType.in.length > 0
      ) {
        filters.and.push({
          and: {
            subType: this.filters.subType,
          },
        });
      }
      // SQUARE FOOT
      if (this.filters?.squareFoot) {
        filters.and.push(
          ...this.convertFilter('squareFoot', this.filters.squareFoot)
        );
      }
      // LOT SIZE
      if (this.filters?.lotSize) {
        filters.and.push(
          ...this.convertFilter('lotSize', this.filters.lotSize)
        );
      }
      // YEAR BUILT
      if (this.filters?.yearBuilt) {
        filters.and.push(
          ...this.convertFilter('yearBuilt', this.filters.yearBuilt)
        );
      }
      // CBSAS
      let cbsas: string[] | undefined = undefined;
      if (this.filters?.CBSAs) {
        cbsas = this.filters.CBSAs.map((cbsa) => JSON.stringify(cbsa));
      }
      // fetch properties
      this.store.dispatch(
        fetchPropertiesAction({
          filter: filters,
          limit: PROPERTIES_PER_PAGE,
          offset: 0,
          cbsas: cbsas,
          sort: [
            {
              field: 'INSERTED_AT',
              order: 'DESC',
            },
          ],
        })
      );
      this.firstChange = false;
      if (closeModal) this.openModal = false;
    } catch (err) {
      throw err;
    }
  }
}
